import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, ChevronDown, Bell, Menu, Plus, User, Settings as SettingsIcon, LogOut, Check, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import api from "@/lib/api";
import { SidebarInner } from "./Sidebar";
import { CommandPalette } from "@/components/CommandPalette";
import { DemoBadge, useDemo } from "@/components/demo/DemoUi";
import { DemoJourneyButton } from "@/components/demo/DemoJourney";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const initials = (name = "") =>
  name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "U";

export const TopBar = () => {
  const { t, lang, setLang } = useLang();
  const { user, memberships, activeMembership, activeOrgId, setActiveOrgId, logout } = useAuth();
  const { singleMembership } = useDemo();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadNotifs = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const { data } = await api.get(`/organizations/${activeOrgId}/notifications`);
      setNotifs(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* noop */ }
  }, [activeOrgId]);

  useEffect(() => {
    loadNotifs();
    const id = setInterval(loadNotifs, 45000);
    return () => clearInterval(id);
  }, [loadNotifs]);

  const markRead = async (n) => {
    if (!n.read) {
      try { await api.post(`/organizations/${activeOrgId}/notifications/${n.id}/read`); } catch { /* noop */ }
    }
    if (n.campaign_id) navigate("/campaigns");
    loadNotifs();
  };
  const markAllRead = async () => {
    try { await api.post(`/organizations/${activeOrgId}/notifications/read-all`); } catch { /* noop */ }
    loadNotifs();
  };

  const active = activeMembership;

  return (
    <div className="sticky top-0 z-20 px-4 pt-4 sm:px-6 lg:px-10">
      <header
        data-testid="app-topbar"
        className="flex h-14 items-center gap-3 rounded-2xl border border-lodge-hairline bg-white/80 px-3 shadow-lodge backdrop-blur-xl sm:gap-4 sm:px-4"
      >
      {/* Mobile menu */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetTrigger asChild>
          <button
            data-testid="mobile-menu-trigger"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-lodge-hairline bg-white text-lodge-ardoise transition-colors hover:bg-lodge-ivory lg:hidden"
          >
            <Menu size={18} />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[284px] border-0 p-0" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Navigation Lodge &amp; Co</SheetTitle>
          <SidebarInner onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Workspace switcher — a single-membership (non-admin) user sees a static
          organization name (no dropdown, nothing implying other orgs exist). */}
      {singleMembership ? (
        <div
          data-testid="workspace-static"
          className="flex items-center gap-2.5 rounded-xl border border-lodge-hairline bg-white px-3 py-1.5 text-left"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-lodge-deep text-xs font-bold text-white">
            {(active?.org.name || "L").charAt(0)}
          </span>
          <span className="hidden leading-tight sm:block">
            <span className="block max-w-[190px] truncate text-sm font-semibold text-[#17201A]">
              {active?.org.name || "Organisation"}
            </span>
            <span className="block text-[11px] text-[#64748B]">{active?.org.type}</span>
          </span>
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="workspace-switcher"
              className="flex items-center gap-2.5 rounded-xl border border-lodge-hairline bg-white px-3 py-1.5 text-left transition-colors hover:border-[#CBD5E1]"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-lodge-deep text-xs font-bold text-white">
                {(active?.org.name || "L").charAt(0)}
              </span>
              <span className="hidden leading-tight sm:block">
                <span className="block max-w-[190px] truncate text-sm font-semibold text-[#17201A]">
                  {active?.org.name || "Organisation"}
                </span>
                <span className="block text-[11px] text-[#64748B]">{active?.org.type}</span>
              </span>
              <ChevronDown size={16} className="text-[#94A3B8]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72">
            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-[#64748B]">
              {t("workspace")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {user?.is_platform_admin && (
              <>
                <DropdownMenuItem
                  data-testid="workspace-admin"
                  onClick={() => navigate("/admin")}
                  className="flex items-center gap-2 py-2 font-semibold text-[#1E3D2B]"
                >
                  <ShieldCheck size={15} />
                  <span>Administration plateforme</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {memberships.map((m) => (
              <DropdownMenuItem
                key={m.org.id}
                data-testid={`workspace-option-${m.org.id}`}
                onClick={() => setActiveOrgId(m.org.id)}
                className="flex items-center justify-between gap-2 py-2"
              >
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-sm font-medium text-[#17201A]">{m.org.name}</span>
                  <span className="text-[11px] text-[#64748B]">{m.org.type} · {m.role}</span>
                </span>
                {m.org.id === activeOrgId && <Check size={15} className="text-[#3E7256]" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="workspace-create"
              onClick={() => navigate("/onboarding")}
              className="flex items-center gap-2 py-2 text-[#3E7256]"
            >
              <Plus size={15} />
              <span className="text-sm font-medium">Créer une organisation</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Persistent Demo badge (visible across the Demo journey). */}
      <DemoBadge />
      <DemoJourneyButton />

      {/* Search — opens the ⌘K command palette */}
      <div className="relative hidden min-w-0 flex-1 md:block">
        <button
          type="button"
          data-testid="global-search"
          onClick={() => setPaletteOpen(true)}
          className="flex h-9 w-full max-w-md items-center gap-2.5 rounded-xl border border-lodge-hairline bg-lodge-ivory pl-3 pr-2 text-left text-sm text-[#94A3B8] transition-colors hover:border-lodge-mint hover:bg-white"
        >
          <Search size={16} className="shrink-0 text-[#94A3B8]" />
          <span className="flex-1 truncate">{t("search_placeholder")}</span>
          <kbd className="hidden shrink-0 rounded border border-lodge-hairline bg-white px-1.5 py-0.5 font-mono text-[10px] text-[#94A3B8] xl:block">
            ⌘K
          </kbd>
        </button>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Mobile search trigger */}
        <button
          type="button"
          data-testid="global-search-mobile"
          onClick={() => setPaletteOpen(true)}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-lodge-hairline bg-white text-lodge-ardoise transition-colors hover:bg-lodge-ivory md:hidden"
        >
          <Search size={17} />
        </button>

        {/* Language toggle */}
        <div
          data-testid="lang-toggle"
          className="flex items-center rounded-xl border border-lodge-hairline bg-white p-0.5 text-xs font-semibold"
        >
          {["fr", "en"].map((l) => (
            <button
              key={l}
              data-testid={`lang-btn-${l}`}
              onClick={() => setLang(l)}
              className={`rounded-md px-2.5 py-1 uppercase transition-colors ${
                lang === l ? "bg-lodge-deep text-white" : "text-[#64748B] hover:text-[#17201A]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Ask Lodge */}
        <button
          data-testid="ask-lodge-trigger"
          onClick={() => navigate("/ask-lodge")}
          className="hidden items-center gap-1.5 rounded-xl bg-lodge-deep px-3.5 py-2 text-sm font-semibold text-white shadow-lodge-sm transition-all hover:bg-lodge-deep-soft hover:shadow-lodge sm:flex"
        >
          <Sparkles size={15} className="text-lodge-mint" />
          {t("ai_trigger")}
        </button>

        {/* Notifications */}
        <DropdownMenu onOpenChange={(o) => o && loadNotifs()}>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="notifications-btn"
              className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-lodge-hairline bg-white text-[#475569] transition-colors hover:bg-lodge-ivory"
            >
              <Bell size={17} />
              {unread > 0 && (
                <span data-testid="notif-badge" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#D97706] px-1 text-[9px] font-bold text-white">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <div className="flex items-center justify-between px-2 py-1">
              <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
              {unread > 0 && (
                <button data-testid="notif-read-all" onClick={markAllRead} className="text-xs font-semibold text-[#3E7256] hover:underline">
                  Tout marquer lu
                </button>
              )}
            </div>
            <DropdownMenuSeparator />
            {notifs.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-slate-400" data-testid="notif-empty">Aucune notification</div>
            ) : (
              notifs.slice(0, 8).map((n) => (
                <DropdownMenuItem
                  key={n.id}
                  data-testid={`notif-${n.id}`}
                  onClick={() => markRead(n)}
                  className={`flex cursor-pointer flex-col items-start gap-0.5 ${!n.read ? "bg-[#F1F7F3]" : ""}`}
                >
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-[#17201A]">
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#D97706]" />}{n.title}
                  </span>
                  <span className="text-xs text-slate-500">{n.body}</span>
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-lodge-deep text-xs font-bold text-white ring-2 ring-transparent transition-all hover:ring-[#5C9873]/30"
            >
              {initials(user?.name)}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <div className="text-sm font-semibold text-[#17201A]">{user?.name}</div>
              <div className="text-[11px] font-normal text-[#64748B]">{user?.email}</div>
              {user?.is_platform_admin && (
                <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-lodge-deep px-2 py-0.5 text-[10px] font-semibold text-white">
                  <ShieldCheck size={11} /> Lodge &amp; Co Super Admin
                </div>
              )}
              {active && (
                <div className="mt-1.5 inline-block rounded-full bg-[#ECF3EC] px-2 py-0.5 text-[10px] font-semibold text-[#3E7256]">
                  {active.role}
                </div>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {user?.is_platform_admin && (
              <>
                <DropdownMenuItem
                  data-testid="user-menu-admin"
                  onClick={() => navigate("/admin")}
                  className="font-semibold text-[#1E3D2B] focus:text-[#1E3D2B]"
                >
                  <ShieldCheck size={15} className="mr-2" /> Lodge &amp; Co Admin
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem data-testid="user-menu-profile" onClick={() => navigate("/profile")}>
              <User size={15} className="mr-2" /> {t("profile_menu")}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="user-menu-settings" onClick={() => navigate("/settings")}>
              <SettingsIcon size={15} className="mr-2" /> Paramètres
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="user-menu-logout"
              onClick={logout}
              className="text-rose-600 focus:text-rose-600"
            >
              <LogOut size={15} className="mr-2" /> Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      </header>
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
    </div>
  );
};
